import { useEffect, useState, type ReactNode } from "react";
import { Box, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import { useTranslation } from "react-i18next";
import AppIconButton from "../form/AppIconButton";
import SlideOverPanel, { type SlideOverSize } from "../SlideOverPanel";

export type AppSlideOverProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  /** Footer actions (Save / Cancel). */
  actions?: ReactNode;
  /**
   * Content-fit width: sm≈480, md≈640, lg≈800, xl≈1040.
   * Use lg/xl for dense admin forms; sm/md for short create forms.
   */
  size?: SlideOverSize;
  /** Open already expanded across the screen. */
  defaultFullscreen?: boolean;
  backgroundColor?: string;
};

/**
 * Standard create/update slide-over: right panel, content-fit width, fullscreen toggle.
 * Use for entity CRUD instead of AppDialog or separate edit pages.
 * Delete stays on confirm dialogs (`useDeleteConfirm`).
 */
export default function AppSlideOver({
  open,
  onClose,
  title,
  children,
  actions,
  size = "md",
  defaultFullscreen = false,
  backgroundColor,
}: AppSlideOverProps) {
  const { t } = useTranslation();
  const [fullscreen, setFullscreen] = useState(defaultFullscreen);

  useEffect(() => {
    if (!open) setFullscreen(defaultFullscreen);
  }, [open, defaultFullscreen]);

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      size={size}
      fullscreen={fullscreen}
      backgroundColor={backgroundColor}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Typography variant="h6" component="h2" fontWeight={650} noWrap sx={{ minWidth: 0, flex: 1 }}>
            {title}
          </Typography>
          <Stack direction="row" spacing={0.25} alignItems="center" flexShrink={0}>
            <AppIconButton
              size="small"
              aria-label={fullscreen ? t("common.exitFullscreen") : t("common.expandFullscreen")}
              title={fullscreen ? t("common.exitFullscreen") : t("common.expandFullscreen")}
              onClick={() => setFullscreen((v) => !v)}
            >
              {fullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </AppIconButton>
            <AppIconButton size="small" aria-label={t("common.close")} title={t("common.close")} onClick={onClose}>
              <CloseIcon fontSize="small" />
            </AppIconButton>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 2, py: 2 }}>{children}</Box>

        {actions != null ? (
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            flexWrap="wrap"
            useFlexGap
            sx={{
              px: 2,
              py: 1.5,
              borderTop: 1,
              borderColor: "divider",
              flexShrink: 0,
            }}
          >
            {actions}
          </Stack>
        ) : null}
      </Box>
    </SlideOverPanel>
  );
}
